import React from "react";
import { connect } from "react-redux";
import { NumericFormat } from "react-number-format";

import { Button } from "components/button/Button";
import { Alert } from "components/alert/Alert";
import { getVolumeAnalysis } from "views/invoices/Services";
import {
  AccountingPeriodPicker,
  AccountPeriodPickerState
} from "./AccountingPeriodPicker";
import { AccountingPeriodStats } from "./AccountingPeriodStats";
import {
  CalculatedVolumeAnalysis,
  VolumeAnalysisDatainner
} from "./VolumeAnalysisDatainner";
import { VolumeAnalysisData } from "./VolumeAnalysisData";

import "./VolumeAnalysis.scss";

type VolumeAnalysisProps = {
  customerId: string;
  dispatch: any;
};

type VolumeAnalysisState = {
  volumeAnalyses: VolumeAnalysisData[];
  isVoucherCountExpanded: boolean;
  isTransactionCountExpanded: boolean;
  aggregatedPeriod1: CalculatedVolumeAnalysis | null;
  aggregatedPeriod2: CalculatedVolumeAnalysis | null;
  periodsDiff: VolumeAnalysisDatainner | null;
  periodsOverlap: boolean;
};

export class VolumeAnalysis extends React.Component<
  VolumeAnalysisProps,
  VolumeAnalysisState
> {
  period1DatePicker: React.RefObject<AccountingPeriodPicker> | null;
  period2DatePicker: React.RefObject<AccountingPeriodPicker> | null;
  setPeriod1DatePicker: (element: AccountingPeriodPicker) => void;
  setPeriod2DatePicker: (element: AccountingPeriodPicker) => void;

  constructor(props: any) {
    super(props);

    this.period2DatePicker = null;
    this.period1DatePicker = null;

    this.setPeriod1DatePicker = (element: any) => {
      this.period1DatePicker = element;
    };

    this.setPeriod2DatePicker = (element: any) => {
      this.period2DatePicker = element;
    };

    this.state = {
      volumeAnalyses: [],
      isVoucherCountExpanded: false,
      isTransactionCountExpanded: false,
      aggregatedPeriod1: null,
      aggregatedPeriod2: null,
      periodsDiff: null,
      periodsOverlap: false
    };
  }

  componentDidMount() {
    this.props.dispatch(
      getVolumeAnalysis(this.props.customerId, (x: any) =>
        this.getVolumeAnalysisCallback(x)
      )
    );
  }

  getVolumeAnalysisCallback(volumeAnalyses: VolumeAnalysisData[]) {
    const minVersion = "0.8";
    const compatibleVolumeAnalyses = volumeAnalyses.filter(
      (va) => va.algorithm_version >= minVersion
    );
    const incompatible = volumeAnalyses.filter(
      (va) => va.algorithm_version < minVersion
    );
    if (incompatible.length > 0) {
      console.warn("Skipping incompatible analyses:", incompatible);
    }
    this.setState({
      volumeAnalyses: compatibleVolumeAnalyses
    });
  }

  amount(value: any) {
    return (
      <NumericFormat
        value={value}
        displayType={"text"}
        decimalSeparator={","}
        thousandSeparator={" "}
        decimalScale={0}
        fixedDecimalScale={true}
      />
    );
  }

  calcVolumeAnalysis(
    state: AccountPeriodPickerState
  ): CalculatedVolumeAnalysis {
    function notEmpty<TValue>(
      value: TValue | null | undefined
    ): value is TValue {
      return value !== null && value !== undefined;
    }

    const startMonthAsNumber = parseInt(state.startMonth);
    const endYear =
      parseInt(state.startYear) +
      Math.floor((startMonthAsNumber + state.monthCount - 2) / 12);
    const endMonthAsNumber =
      ((startMonthAsNumber + state.monthCount - 2) % 12) + 1;
    const endMonth = endMonthAsNumber.toString().padStart(2, "0");
    const min = `${state.startYear}${state.startMonth}`;
    const max = `${endYear}${endMonth}`;

    const filterAccountPeriodStats = (): VolumeAnalysisDatainner[] => {
      return this.state.volumeAnalyses
        .flatMap((x) =>
          Object.keys(x.period_stats).map((k) => {
            if (min <= k && k <= max) {
              return x.period_stats[k];
            }

            return null;
          })
        )
        .filter(notEmpty);
    };

    const accountPeriodStats = filterAccountPeriodStats();

    const agg = (
      a: VolumeAnalysisDatainner,
      b: VolumeAnalysisDatainner
    ): VolumeAnalysisDatainner => {
      // Foreach series, generate the sum of the voucher counts/transaction counts and store in "a"'s counts
      Object.keys(b.voucher_count_per_series).forEach((key) => {
        a.voucher_count_per_series[key] = a.voucher_count_per_series[key] || 0;
        a.voucher_count_per_series[key] += b.voucher_count_per_series[key];
      });
      Object.keys(b.transaction_count_per_series).forEach((key) => {
        a.transaction_count_per_series[key] =
          a.transaction_count_per_series[key] || 0;
        a.transaction_count_per_series[key] +=
          b.transaction_count_per_series[key];
      });

      const voucher_count_total = Object.keys(
        a.voucher_count_per_series
      ).reduce(
        (acc: number, key: string) => acc + a.voucher_count_per_series[key],
        0
      );
      const transaction_count_total = Object.keys(
        a.transaction_count_per_series
      ).reduce(
        (acc: number, key: string) => acc + a.transaction_count_per_series[key],
        0
      );

      return {
        voucher_count_per_series: a.voucher_count_per_series,
        voucher_count_total: voucher_count_total,
        transaction_count_total: transaction_count_total,
        transaction_count_per_series: a.transaction_count_per_series,
        revenue: a.revenue + b.revenue,
        gross_margin: a.gross_margin + b.gross_margin,
        ebit_revenue: a.ebit_revenue + b.ebit_revenue,
        balance_sheet_total: a.balance_sheet_total + b.balance_sheet_total,
        zero_account_transaction_count:
          a.zero_account_transaction_count + b.zero_account_transaction_count
      };
    };
    const aggAccountPeriodStats = accountPeriodStats.reduce(agg, {
      voucher_count_total: 0,
      voucher_count_per_series: {},
      transaction_count_total: 0,
      transaction_count_per_series: {},
      revenue: 0,
      gross_margin: 0,
      ebit_revenue: 0,
      balance_sheet_total: 0,
      zero_account_transaction_count: 0
    });
    return {
      ...aggAccountPeriodStats,
      periodStart: min,
      periodEnd: max
    };
  }

  calcPeriodDiff(
    p1: CalculatedVolumeAnalysis | null,
    p2: CalculatedVolumeAnalysis | null
  ): VolumeAnalysisDatainner {
    const defaultValue = {
      voucher_count_total: 0,
      voucher_count_per_series: {},
      transaction_count_total: 0,
      transaction_count_per_series: {},
      revenue: 0,
      gross_margin: 0,
      ebit_revenue: 0,
      balance_sheet_total: 0,
      zero_account_transaction_count: 0
    };

    if (p1 == null || p2 == null) {
      this.setState({ periodsOverlap: false });
      return defaultValue;
    }

    let period1 = p1;
    let period2 = p2;
    // Switch p1 and p2 if p2 > p1
    if (p1.periodStart < p2.periodStart) {
      console.debug("Switching p1, p2");
      const temp = p2;
      period2 = p1;
      period1 = temp;
    }

    // Calculate whether they overlap
    if (period1.periodStart <= period2.periodEnd) {
      this.setState({ periodsOverlap: true });
    } else {
      this.setState({ periodsOverlap: false });
    }

    const asPercentage = (to: number, from: number): number => {
      if (from !== 0) {
        const change = to - from;
        return 100 * (change / from);
      }
      return 0;
    };

    return {
      voucher_count_total: asPercentage(
        period1.voucher_count_total,
        period2.voucher_count_total
      ),
      transaction_count_total: asPercentage(
        period1.transaction_count_total,
        period2.transaction_count_total
      ),
      voucher_count_per_series: {},
      transaction_count_per_series: {},
      revenue: asPercentage(period1.revenue, period2.revenue),
      gross_margin: asPercentage(period1.gross_margin, period2.gross_margin),
      ebit_revenue: asPercentage(period1.ebit_revenue, period2.ebit_revenue),
      balance_sheet_total: asPercentage(
        period1.balance_sheet_total,
        period2.balance_sheet_total
      ),
      zero_account_transaction_count: asPercentage(
        period1.zero_account_transaction_count,
        period2.zero_account_transaction_count
      )
    };
  }

  renderPeriod1(state: AccountPeriodPickerState): void {
    const period1Analysis = this.calcVolumeAnalysis(state);
    this.setState({
      aggregatedPeriod1: period1Analysis,
      periodsDiff: this.calcPeriodDiff(
        period1Analysis,
        this.state.aggregatedPeriod2
      )
    });
  }

  renderPeriod2(state: AccountPeriodPickerState): void {
    const period2Analysis = this.calcVolumeAnalysis(state);
    this.setState({
      aggregatedPeriod2: period2Analysis,
      periodsDiff: this.calcPeriodDiff(
        this.state.aggregatedPeriod1,
        period2Analysis
      )
    });
  }

  render() {
    const transactionCountExpandButton = (
      <Button
        color="tertiary"
        size="sm"
        onClick={() => {
          this.setState({
            isTransactionCountExpanded: !this.state.isTransactionCountExpanded
          });
        }}
      >
        {(this.state.isTransactionCountExpanded && "-") || "+"}
      </Button>
    );

    const voucherCountExpandButton = (
      <Button
        color="tertiary"
        size="sm"
        onClick={() => {
          this.setState({
            isVoucherCountExpanded: !this.state.isVoucherCountExpanded
          });
        }}
      >
        {(this.state.isVoucherCountExpanded && "-") || "+"}
      </Button>
    );

    const availableYears = Array.from(
      new Set(
        this.state.volumeAnalyses.flatMap((v) => [
          v.rar_to.slice(0, 4),
          v.rar_from.slice(0, 4)
        ])
      )
    )
      .sort()
      .reverse();

    const onPeriod1DateChanged = (state: AccountPeriodPickerState) => {
      this.renderPeriod1(state);
      //TODO: Update year/months
    };

    const onPeriod2DateChanged = (state: AccountPeriodPickerState) => {
      this.renderPeriod2(state);
    };

    return (
      this.state.volumeAnalyses.length > 0 && (
        <div className="volumeAnalysis">
          <h1>Volymanalys</h1>
          <div className="warningsWrapper">
            {this.state.periodsOverlap && (
              <Alert variant={"warning"} onDismiss={undefined}>
                De valda perioderna överlappar
              </Alert>
            )}
          </div>

          <div className="volumeAnalysisContainer">
            <div></div>
            <div>
              <AccountingPeriodPicker
                ref={this.setPeriod1DatePicker}
                key={"endPeriodPicker"}
                years={availableYears}
                onChange={onPeriod1DateChanged}
              ></AccountingPeriodPicker>
              <span className="periodSpan" title="Perioden som avses">
                {this.state.aggregatedPeriod1?.periodStart} -{" "}
                {this.state.aggregatedPeriod1?.periodEnd}
              </span>
            </div>
            <div>
              <AccountingPeriodPicker
                ref={this.setPeriod2DatePicker}
                key={"startPeriodPicker"}
                years={availableYears}
                onChange={onPeriod2DateChanged}
              ></AccountingPeriodPicker>
              <span className="periodSpan" title="Perioden som avses">
                {this.state.aggregatedPeriod2?.periodStart} -{" "}
                {this.state.aggregatedPeriod2?.periodEnd}
              </span>
            </div>
            <div>Förändring (%)</div>

            <div>
              <div>Antal verifikat</div>
              <div>Antal rader</div>
              <div>Omsättning</div>
              <div>Bruttovinst</div>
              <div>EBIT-vinst</div>
              <div>Balansomslutning</div>
              <div>Antal nollrader</div>
            </div>
            {(this.state.aggregatedPeriod1 && (
              <AccountingPeriodStats
                VolumeAnalysisDatainner={this.state.aggregatedPeriod1}
              ></AccountingPeriodStats>
            )) || <div></div>}
            {(this.state.aggregatedPeriod2 && (
              <AccountingPeriodStats
                VolumeAnalysisDatainner={this.state.aggregatedPeriod2}
              ></AccountingPeriodStats>
            )) || <div></div>}
            {(this.state.aggregatedPeriod1 &&
              this.state.aggregatedPeriod2 &&
              this.state.periodsDiff && (
                <AccountingPeriodStats
                  VolumeAnalysisDatainner={this.state.periodsDiff}
                ></AccountingPeriodStats>
              )) || <div></div>}
          </div>

          <table>
            <thead>
              <tr>
                <th></th>
                {this.state.volumeAnalyses.map((volumeAnalysis, index) => {
                  return (
                    <th key={`year_${index}`}>
                      <span title="Perioden som avses">
                        {volumeAnalysis.rar_from}–{volumeAnalysis.rar_to}
                      </span>{" "}
                      <small title="Senaste verifikationsdatum">
                        ({volumeAnalysis.last_ver_date})
                      </small>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>
                  Antal verifikat&nbsp;
                  {voucherCountExpandButton}
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  const agg = (acc: number, x: string) => {
                    return acc + volumeAnalysis.voucher_count_per_series[x];
                  };
                  return (
                    <td>
                      <table>
                        <tbody>
                          <tr>
                            <th>Summa</th>
                            <th className="text-right">
                              {this.amount(
                                Object.keys(
                                  volumeAnalysis.voucher_count_per_series
                                ).reduce(agg, 0)
                              )}
                            </th>
                          </tr>
                          {this.state.isVoucherCountExpanded &&
                            Object.keys(
                              volumeAnalysis.voucher_count_per_series
                            ).map((series) => {
                              return (
                                <tr key={`voucher_count_per_series_${series}`}>
                                  <td>{series}</td>
                                  <td className="text-right">
                                    {this.amount(
                                      volumeAnalysis.voucher_count_per_series[
                                        series
                                      ]
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  Antal transaktionsrader&nbsp;
                  {transactionCountExpandButton}
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  const agg = (acc: any, x: any) => {
                    return acc + volumeAnalysis.transaction_count_per_series[x];
                  };
                  return (
                    <td>
                      <table>
                        <tbody>
                          <tr>
                            <th>Summa</th>
                            <th className="text-right">
                              {this.amount(
                                Object.keys(
                                  volumeAnalysis.transaction_count_per_series
                                ).reduce(agg, 0)
                              )}
                            </th>
                          </tr>
                          {this.state.isTransactionCountExpanded &&
                            Object.keys(
                              volumeAnalysis.transaction_count_per_series
                            ).map((series) => {
                              return (
                                <tr
                                  key={`transaction_count_per_series_${series}`}
                                >
                                  <td>{series}</td>
                                  <td className="text-right">
                                    {
                                      volumeAnalysis
                                        .transaction_count_per_series[series]
                                    }
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  <span title="Summan av alla konton 3xxx">Omsättning</span>
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  return (
                    <td className="text-right">
                      {this.amount(volumeAnalysis.revenue)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  <span title="Differensen av alla konton 3xxx - 4xxx">
                    Bruttovinst
                  </span>
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  return (
                    <td className="text-right">
                      {this.amount(volumeAnalysis.gross_margin)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  <span title="Summan av alla konton 3xxx t.o.m. 8xxx">
                    EBIT-vinst
                  </span>
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  return (
                    <td className="text-right">
                      {this.amount(volumeAnalysis.ebit_revenue)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  <span title="Summan av alla konton 1xxx">
                    Balansomslutning
                  </span>
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  return (
                    <td className="text-right">
                      {this.amount(volumeAnalysis.balance_sheet_total)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th>
                  <span title="Systemet som har genererat SIE-filen som ligger till grund för datan">
                    Program
                  </span>
                </th>
                {this.state.volumeAnalyses.map((volumeAnalysis) => {
                  return <td>{volumeAnalysis.program}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )
    );
  }
}
export default connect((state) => {
  return {};
})(VolumeAnalysis);
